import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  HelpCircle,
  MessageSquare,
  Bookmark,
  Share2,
  Sparkles,
  ThumbsUp,
  CornerDownRight,
  Send,
  Trash2,
  Edit2,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Briefcase,
  GraduationCap,
  MoreVertical,
  ShieldAlert,
} from 'lucide-react'
import { colors, radii, typography } from '../../styles/tokens'
import {
  qaApi,
  type QuestionDetail,
  type QuestionAnswer,
  type AnswerReply,
} from '../../api/qa'
import { useAuthStore } from '../../stores/authStore'
import { Avatar } from '../../components/shared/Avatar'
import { TimeAgo } from '../../components/shared/TimeAgo'
import { VerifiedBadge } from '../../components/shared/VerifiedBadge'
import { ReportModal } from '../../components/alumni/ReportModal'
import { EmptyState } from '../../components/shared/EmptyState'
import toast from 'react-hot-toast'

export function QuestionDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)

  const [question, setQuestion] = useState<QuestionDetail | null>(null)
  const [loading, setLoading] = useState(true)

  // Question save state
  const [isQuestionSaved, setIsQuestionSaved] = useState(false)
  const [saveBusy, setSaveBusy] = useState(false)

  // Report modal state
  const [reportModal, setReportModal] = useState<{
    isOpen: boolean
    targetType: 'question' | 'answer'
    targetId: string
    title?: string
  }>({
    isOpen: false,
    targetType: 'question',
    targetId: '',
  })

  // Top-level answer composer state
  const [newAnswerContent, setNewAnswerContent] = useState('')
  const [submittingAnswer, setSubmittingAnswer] = useState(false)

  // Answer editing state
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  // Reply composer state (keyed by answerId)
  const [activeReplyAnswerId, setActiveReplyAnswerId] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [submittingReply, setSubmittingReply] = useState(false)

  const fetchQuestion = useCallback(async () => {
    if (!id) return
    try {
      const res = await qaApi.getQuestion(id)
      const data = res.data.data
      setQuestion(data)
      setIsQuestionSaved(!!data.is_saved)
    } catch (err) {
      console.error('Failed to load question detail:', err)
      toast.error('Question not found or deleted')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchQuestion()
  }, [fetchQuestion])

  // Toggle Save Question
  const handleToggleSaveQuestion = async () => {
    if (!question || saveBusy) return
    setSaveBusy(true)
    const prev = isQuestionSaved
    setIsQuestionSaved(!prev)
    try {
      const res = await qaApi.toggleSave('question', question.id)
      setIsQuestionSaved(res.data.is_saved)
      toast.success(res.data.is_saved ? 'Question saved to bookmarks' : 'Removed from saved')
    } catch {
      setIsQuestionSaved(prev)
      toast.error('Failed to update bookmark')
    } finally {
      setSaveBusy(false)
    }
  }

  // Toggle Save Answer
  const handleToggleSaveAnswer = async (answerId: string) => {
    if (!question) return
    try {
      const res = await qaApi.toggleSave('answer', answerId)
      const saved = res.data.is_saved
      setQuestion({
        ...question,
        answers: question.answers.map((a) => (a.id === answerId ? { ...a, is_saved: saved } : a)),
      })
      toast.success(saved ? 'Answer bookmarked' : 'Answer removed from saved')
    } catch {
      toast.error('Failed to update bookmark')
    }
  }

  // Helpful Vote
  const handleVoteAnswer = async (answerId: string) => {
    if (!question) return
    try {
      const res = await qaApi.voteAnswer(answerId)
      const { voted, helpful_count } = res.data
      setQuestion({
        ...question,
        answers: question.answers.map((a) =>
          a.id === answerId ? { ...a, has_voted: voted, helpful_count } : a
        ),
      })
      toast.success(voted ? 'Marked as helpful' : 'Vote removed')
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to register vote')
    }
  }

  // Share
  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: question?.title,
          text: `Read this career advice discussion on Campnect: "${question?.title}"`,
          url,
        })
        return
      } catch {}
    }
    await navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard')
  }

  // Submit Answer
  const handleSubmitAnswer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!question) return
    if (newAnswerContent.trim().length < 20) {
      toast.error('Answer must be at least 20 characters.')
      return
    }
    if (submittingAnswer) return
    setSubmittingAnswer(true)
    try {
      await qaApi.createAnswer(question.id, { content: newAnswerContent.trim() })
      toast.success('Your answer has been published!')
      setNewAnswerContent('')
      await fetchQuestion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to submit answer.')
    } finally {
      setSubmittingAnswer(false)
    }
  }

  // Start Edit Answer
  const handleStartEditAnswer = (ans: QuestionAnswer) => {
    setEditingAnswerId(ans.id)
    setEditingContent(ans.content)
  }

  // Save Edit Answer
  const handleSaveEditAnswer = async (answerId: string) => {
    if (editingContent.trim().length < 20) {
      toast.error('Answer must be at least 20 characters.')
      return
    }
    setSavingEdit(true)
    try {
      await qaApi.editAnswer(answerId, { content: editingContent.trim() })
      toast.success('Answer updated')
      setEditingAnswerId(null)
      await fetchQuestion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update answer')
    } finally {
      setSavingEdit(false)
    }
  }

  // Delete Answer
  const handleDeleteAnswer = async (answerId: string) => {
    if (!window.confirm('Are you sure you want to delete this answer?')) return
    try {
      await qaApi.deleteAnswer(answerId)
      toast.success('Answer deleted')
      await fetchQuestion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete answer')
    }
  }

  // Submit Follow-up Reply
  const handleSubmitReply = async (answerId: string) => {
    if (replyContent.trim().length < 2) {
      toast.error('Reply must be at least 2 characters.')
      return
    }
    if (submittingReply) return
    setSubmittingReply(true)
    try {
      await qaApi.replyAnswer(answerId, { content: replyContent.trim() })
      toast.success('Reply posted')
      setReplyContent('')
      setActiveReplyAnswerId(null)
      await fetchQuestion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to submit reply')
    } finally {
      setSubmittingReply(false)
    }
  }

  // Delete Question (if author)
  const handleDeleteQuestion = async () => {
    if (!question) return
    if (!window.confirm('Are you sure you want to delete this entire question?')) return
    try {
      await qaApi.deleteQuestion(question.id)
      toast.success('Question deleted')
      navigate('/app/alumni')
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete question')
    }
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 840, margin: '0 auto', padding: '30px 20px', width: '100%' }}>
        <div style={{ height: 240, borderRadius: 24, background: 'var(--color-surface)', animation: 'pulse 1.5s infinite' }} />
      </div>
    )
  }

  if (!question) {
    return (
      <div style={{ maxWidth: 840, margin: '40px auto', padding: '20px', textAlign: 'center' }}>
        <EmptyState
          icon={<HelpCircle size={40} color="var(--color-text-muted)" />}
          title="Question Not Found"
          description="This question may have been removed or does not exist."
          action={
            <button
              onClick={() => navigate('/app/alumni')}
              style={{
                padding: '8px 18px',
                borderRadius: radii.pill,
                border: 'none',
                background: '#111114',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Back to Q&A Feed
            </button>
          }
        />
      </div>
    )
  }

  // Calculate highest helpful count for "Most Helpful Answer" label
  const maxHelpfulVotes = Math.max(...(question.answers || []).map((a) => a.helpful_count || 0), 0)

  return (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--color-bg)',
        paddingBottom: 'calc(var(--nav-height) + 40px)',
      }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px', width: '100%' }}>
        {/* Back Link */}
        <button
          onClick={() => navigate('/app/alumni?tab=questions')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '14px',
            padding: 0,
          }}
        >
          <ArrowLeft size={15} /> Back to Q&A Feed
        </button>

        {/* ========================================================= */}
        {/* QUESTION HEADER CARD                                      */}
        {/* ========================================================= */}
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 24,
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {/* Category Pill + Status + Top Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: '11.5px',
                  fontWeight: 700,
                  fontFamily: typography.fontDisplay,
                  padding: '3px 10px',
                  borderRadius: radii.pill,
                  background: 'var(--color-bg)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {question.category}
              </span>

              {question.answers_count > 0 ? (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#15803D',
                    background: 'rgba(34,197,94,0.1)',
                    padding: '2px 8px',
                    borderRadius: radii.pill,
                  }}
                >
                  <CheckCircle2 size={12} />
                  {question.answers_count} {question.answers_count === 1 ? 'Answer' : 'Answers'}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    padding: '2px 8px',
                    borderRadius: radii.pill,
                  }}
                >
                  Unanswered
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={handleShare}
                title="Share question"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                }}
              >
                <Share2 size={16} />
              </button>

              <button
                onClick={handleToggleSaveQuestion}
                title={isQuestionSaved ? 'Remove from saved' : 'Save question'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: isQuestionSaved ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                }}
              >
                <Bookmark size={17} fill={isQuestionSaved ? 'currentColor' : 'none'} />
              </button>

              <button
                onClick={() =>
                  setReportModal({
                    isOpen: true,
                    targetType: 'question',
                    targetId: question.id,
                    title: question.title,
                  })
                }
                title="Report question"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                }}
              >
                <ShieldAlert size={16} />
              </button>

              {question.is_author && (
                <button
                  onClick={handleDeleteQuestion}
                  title="Delete your question"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#EF4444',
                    padding: '8px',
                    borderRadius: '8px',
                    display: 'flex',
                  }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Student Profile Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar
              src={question.student_avatar}
              name={question.student_name}
              size={44}
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                  {question.student_name}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  @{question.student_username}
                </span>
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                {[question.student_college, question.student_course].filter(Boolean).join(' · ')}
                <span>·</span>
                <TimeAgo date={question.created_at} />
              </div>
            </div>
          </div>

          {/* Title & Description */}
          <div>
            <h1
              style={{
                margin: '0 0 10px',
                fontSize: '22px',
                fontWeight: 800,
                lineHeight: 1.35,
                color: 'var(--color-text-primary)',
                fontFamily: typography.fontDisplay,
              }}
            >
              {question.title}
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: '15px',
                lineHeight: 1.65,
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {question.description}
            </p>
          </div>

          {/* Tags */}
          {question.tags && question.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {question.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    padding: '3px 10px',
                    borderRadius: radii.pill,
                  }}
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Tagged Alumni Section */}
          {question.tagged_alumni && question.tagged_alumni.length > 0 && (
            <div
              style={{
                padding: '14px 16px',
                borderRadius: 16,
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Alumni Tagged in this Question ({question.tagged_alumni.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                {question.tagged_alumni.map((alm) => (
                  <div
                    key={alm.id}
                    onClick={() => navigate(`/app/alumni/profile/${alm.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      borderRadius: radii.md,
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      cursor: 'pointer',
                    }}
                  >
                    <Avatar src={alm.avatar_url} name={alm.name} size={32} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {alm.name}
                        </span>
                        {alm.is_verified === 1 && <VerifiedBadge size={12} />}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {alm.job_title} · {alm.company}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ========================================================= */}
        {/* ANSWERS SECTION                                           */}
        {/* ========================================================= */}
        <div style={{ marginTop: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 800,
                color: 'var(--color-text-primary)',
                fontFamily: typography.fontDisplay,
              }}
            >
              Answers ({question.answers.length})
            </h2>
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Ranked by helpful votes & authoritative insights
            </span>
          </div>

          {/* List of Answers */}
          {question.answers && question.answers.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {question.answers.map((answer, index) => {
                const isAuthorOfAnswer = currentUser?.id === answer.author_id
                const isMostHelpful =
                  maxHelpfulVotes > 0 &&
                  answer.helpful_count === maxHelpfulVotes &&
                  index === 0

                const isEditingThis = editingAnswerId === answer.id

                return (
                  <div
                    key={answer.id}
                    style={{
                      background: 'var(--color-surface)',
                      border: isMostHelpful ? '1.5px solid #111114' : '1px solid var(--color-border)',
                      borderRadius: 22,
                      padding: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      boxShadow: isMostHelpful ? 'var(--shadow-sm)' : 'none',
                    }}
                  >
                    {/* Most Helpful Badge */}
                    {isMostHelpful && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'flex-start',
                          padding: '3px 10px',
                          borderRadius: radii.pill,
                          background: '#111114',
                          color: '#FFFFFF',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                        }}
                      >
                        <Sparkles size={12} />
                        Most Helpful Answer
                      </div>
                    )}

                    {/* Author Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                      <div
                        onClick={() => navigate(`/app/alumni/profile/${answer.author_id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                      >
                        <Avatar
                          src={answer.author_avatar}
                          name={answer.author_name}
                          size={46}
                        />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                              {answer.author_name}
                            </span>
                            {answer.is_verified === 1 && <VerifiedBadge size={14} />}
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                padding: '1px 6px',
                                borderRadius: radii.pill,
                                background: 'var(--color-bg)',
                                border: '1px solid var(--color-border)',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              Alumni Mentor
                            </span>
                          </div>

                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', marginTop: '2px' }}>
                            {answer.author_title} · {answer.author_company}
                          </div>

                          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                            {[answer.author_college, answer.author_grad_year ? `Class of '${String(answer.author_grad_year).slice(-2)}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                            <span> · </span>
                            <TimeAgo date={answer.created_at} />
                          </div>
                        </div>
                      </div>

                      {/* Answer Actions Menu */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                          onClick={() => handleToggleSaveAnswer(answer.id)}
                          title={answer.is_saved ? 'Remove from saved' : 'Save answer'}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: answer.is_saved ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                            padding: '6px',
                          }}
                        >
                          <Bookmark size={16} fill={answer.is_saved ? 'currentColor' : 'none'} />
                        </button>

                        <button
                          onClick={() =>
                            setReportModal({
                              isOpen: true,
                              targetType: 'answer',
                              targetId: answer.id,
                              title: `Answer by ${answer.author_name}`,
                            })
                          }
                          title="Report answer"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            padding: '6px',
                          }}
                        >
                          <ShieldAlert size={15} />
                        </button>

                        {isAuthorOfAnswer && (
                          <>
                            <button
                              onClick={() => handleStartEditAnswer(answer)}
                              title="Edit answer"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--color-text-secondary)',
                                padding: '6px',
                              }}
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              onClick={() => handleDeleteAnswer(answer.id)}
                              title="Delete answer"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#EF4444',
                                padding: '6px',
                              }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Content or Edit Form */}
                    {isEditingThis ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <textarea
                          rows={6}
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: radii.md,
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-bg)',
                            color: 'var(--color-text-primary)',
                            fontSize: '14px',
                            lineHeight: 1.6,
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => setEditingAnswerId(null)}
                            style={{
                              padding: '6px 14px',
                              borderRadius: radii.pill,
                              border: '1px solid var(--color-border)',
                              background: 'transparent',
                              fontSize: '12.5px',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveEditAnswer(answer.id)}
                            disabled={savingEdit}
                            style={{
                              padding: '6px 16px',
                              borderRadius: radii.pill,
                              border: 'none',
                              background: '#111114',
                              color: '#fff',
                              fontSize: '12.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {savingEdit ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '14.5px',
                          lineHeight: 1.65,
                          color: 'var(--color-text-primary)',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {answer.content}
                      </p>
                    )}

                    {/* Bottom Action Bar: Helpful Vote + Reply Action */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: '10px',
                        borderTop: '1px solid var(--color-border)',
                      }}
                    >
                      <button
                        onClick={() => handleVoteAnswer(answer.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 14px',
                          borderRadius: radii.pill,
                          border: '1px solid',
                          borderColor: answer.has_voted ? '#111114' : 'var(--color-border)',
                          background: answer.has_voted ? '#111114' : 'transparent',
                          color: answer.has_voted ? '#FFFFFF' : 'var(--color-text-primary)',
                          fontSize: '12.5px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 140ms ease',
                        }}
                      >
                        <ThumbsUp size={14} fill={answer.has_voted ? 'currentColor' : 'none'} />
                        Helpful ({answer.helpful_count || 0})
                      </button>

                      <button
                        onClick={() =>
                          setActiveReplyAnswerId(
                            activeReplyAnswerId === answer.id ? null : answer.id
                          )
                        }
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '6px 12px',
                          borderRadius: radii.pill,
                          border: '1px solid var(--color-border)',
                          background: 'transparent',
                          color: 'var(--color-text-secondary)',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <CornerDownRight size={13} />
                        Reply to Answer
                      </button>
                    </div>

                    {/* Inline Reply Input Composer */}
                    {activeReplyAnswerId === answer.id && (
                      <div
                        style={{
                          marginTop: '6px',
                          padding: '12px 14px',
                          borderRadius: radii.md,
                          background: 'var(--color-bg)',
                          border: '1px solid var(--color-border)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                          Ask a follow-up question or clarification to {answer.author_name}:
                        </span>
                        <textarea
                          rows={2}
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Type your follow-up reply..."
                          maxLength={2000}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: radii.md,
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text-primary)',
                            fontSize: '13px',
                            fontFamily: 'inherit',
                            resize: 'none',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveReplyAnswerId(null)
                              setReplyContent('')
                            }}
                            style={{
                              padding: '5px 12px',
                              borderRadius: radii.pill,
                              border: '1px solid var(--color-border)',
                              background: 'transparent',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSubmitReply(answer.id)}
                            disabled={submittingReply}
                            style={{
                              padding: '5px 14px',
                              borderRadius: radii.pill,
                              border: 'none',
                              background: '#111114',
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {submittingReply ? 'Sending...' : 'Post Reply'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Follow-up Replies Thread */}
                    {answer.replies && answer.replies.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          marginTop: '4px',
                          paddingLeft: '18px',
                          borderLeft: '2px solid var(--color-border)',
                        }}
                      >
                        <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          Discussion & Follow-ups ({answer.replies.length})
                        </span>

                        {answer.replies.map((rep) => (
                          <div
                            key={rep.id}
                            style={{
                              padding: '10px 12px',
                              borderRadius: radii.md,
                              background: 'var(--color-bg)',
                              border: '1px solid var(--color-border)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Avatar src={rep.author_avatar} name={rep.author_name} size={22} />
                                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                  {rep.author_name}
                                </span>
                                {rep.is_alumnus && (
                                  <span style={{ fontSize: '10px', fontWeight: 700, background: '#111114', color: '#fff', padding: '1px 5px', borderRadius: radii.pill }}>
                                    Alumnus
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                <TimeAgo date={rep.created_at} />
                              </span>
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                              {rep.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 20,
                padding: '30px 20px',
                textAlign: 'center',
              }}
            >
              <HelpCircle size={32} color="var(--color-text-muted)" style={{ marginBottom: 8 }} />
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>No Answers Yet</h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                Tagged alumni have been notified. Answers will appear here once submitted.
              </p>
            </div>
          )}

          {/* ========================================================= */}
          {/* ANSWER COMPOSER SECTION                                   */}
          {/* ========================================================= */}
          <div
            style={{
              marginTop: '30px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 22,
              padding: '22px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Edit2 size={16} style={{ color: 'var(--color-text-primary)' }} />
              <h3
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: 800,
                  color: 'var(--color-text-primary)',
                  fontFamily: typography.fontDisplay,
                }}
              >
                Write an Authoritative Answer
              </h3>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
              Provide clear, practical, and constructive guidance based on your industry experience.
            </p>

            <form onSubmit={handleSubmitAnswer} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <textarea
                rows={5}
                value={newAnswerContent}
                onChange={(e) => setNewAnswerContent(e.target.value)}
                placeholder="Share your professional recommendations, practical preparation steps, recommended tools, or interview experiences..."
                maxLength={10000}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: radii.md,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  {newAnswerContent.length} characters (min 20)
                </span>

                <button
                  type="submit"
                  disabled={submittingAnswer || newAnswerContent.trim().length < 20}
                  style={{
                    padding: '9px 24px',
                    borderRadius: radii.pill,
                    border: 'none',
                    background: '#111114',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: 700,
                    fontFamily: typography.fontDisplay,
                    cursor: submittingAnswer || newAnswerContent.trim().length < 20 ? 'not-allowed' : 'pointer',
                    opacity: submittingAnswer || newAnswerContent.trim().length < 20 ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Send size={13} />
                  {submittingAnswer ? 'Posting...' : 'Submit Answer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.isOpen}
        onClose={() => setReportModal({ ...reportModal, isOpen: false })}
        targetType={reportModal.targetType}
        targetId={reportModal.targetId}
        targetTitle={reportModal.title}
      />
    </div>
  )
}
